// src/pages/prodactions/production-summary/setting/MergeSettings.js
import React, { useState } from "react";
import {
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Paper,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Stack,
    Button
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

// KEY untuk localStorage
export const STORAGE_KEY = 'production_summary_merge_settings';

// Default settings
export const defaultMergeSettings = {
    mergeTradeSector: true,
    mergeClassification: true
};

// Hook untuk mengelola merge settings
export const useMergeSettings = () => {
    const [mergeSettings, setMergeSettings] = useState(() => {
        // Coba load dari localStorage
        const savedSettings = localStorage.getItem(STORAGE_KEY);
        if (savedSettings) {
            try {
                return JSON.parse(savedSettings);
            } catch (e) {
                console.error('Error parsing saved settings:', e);
            }
        }
        // Default values jika tidak ada di localStorage
        return { ...defaultMergeSettings };
    });

    // Simpan settings ke localStorage
    const saveMergeSettings = (settings) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        setMergeSettings(settings);
    };

    return {
        mergeSettings,
        saveMergeSettings
    };
};

// Component Dialog untuk pengaturan merge
export const MergeSettingsDialog = ({ 
    open, 
    onClose, 
    onSave, 
    initialSettings 
}) => {
    const theme = useTheme();
    const [tempSettings, setTempSettings] = useState({ ...initialSettings });

    // Handler untuk mengubah pengaturan sementara
    const handleSettingChange = (setting) => {
        setTempSettings(prev => ({
            ...prev,
            [setting]: !prev[setting]
        }));
    };

    // Handler untuk menyimpan pengaturan
    const handleSave = () => {
        onSave(tempSettings);
        onClose();
    };

    // Reset temp settings ketika dialog dibuka
    React.useEffect(() => {
        if (open) {
            setTempSettings({ ...initialSettings });
        }
    }, [open, initialSettings]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <MergeTypeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6">Pengaturan Merge & Center</Typography>
                    </Box>
                    <IconButton edge="end" color="inherit" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 2 }}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={tempSettings.mergeTradeSector}
                                    onChange={() => handleSettingChange('mergeTradeSector')}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Gabungkan Sel Data Trade Sector
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: 'center', mb: 2 }}
                        />
                        
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={tempSettings.mergeClassification}
                                    onChange={() => handleSettingChange('mergeClassification')}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Gabungkan Sel Data Classification
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: 'center' }}
                        />
                    </FormGroup>
                </Paper>

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Batal
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSave}
                        startIcon={<SaveIcon />}
                        color="primary"
                    >
                        Simpan Pengaturan
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};